import { useEffect, useRef, useState, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useLang } from '../i18n'
import { api, type WorkflowNode, type BidWorkflow } from '../lib/api'

interface NodeDrawerProps {
  node: WorkflowNode
  workflow: BidWorkflow
  onClose: () => void
  onOpenRoom: (room: string) => void
}

export function NodeDrawer({ node, workflow, onClose, onOpenRoom }: NodeDrawerProps) {
  const { t } = useLang()
  const qc = useQueryClient()
  const drawerRef = useRef<HTMLDivElement>(null)
  const [generating, setGenerating] = useState(false)
  const [genError, setGenError] = useState<string | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const stopPoll = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null }
  }, [])

  // Focus trap + Escape close
  useEffect(() => {
    drawerRef.current?.focus()
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('keydown', onKey); stopPoll() }
  }, [onClose, stopPoll])

  const bid = workflow.bidId

  async function patch(update: Parameters<typeof api.patchWorkflowNode>[2]): Promise<void> {
    await api.patchWorkflowNode(bid, node.nodeId, update)
    await qc.invalidateQueries({ queryKey: ['workflow', bid] })
  }

  async function generate(): Promise<void> {
    if (generating) return
    setGenerating(true)
    setGenError(null)
    try {
      const { jobId } = await api.generateNode(bid, node.nodeId)
      // Poll until done/error
      pollRef.current = setInterval(async () => {
        try {
          const job = await api.agentJob(jobId)
          if (job.status === 'done' || job.status === 'error') {
            stopPoll()
            setGenerating(false)
            if (job.status === 'error') {
              setGenError(job.error ?? 'Generation failed')
            } else {
              await qc.invalidateQueries({ queryKey: ['workflow', bid] })
              await qc.invalidateQueries({ queryKey: ['working-docs', bid] })
            }
          }
        } catch { /* network hiccup — keep polling */ }
      }, 3000)
    } catch (e) {
      setGenerating(false)
      setGenError(e instanceof Error ? e.message : String(e))
    }
  }

  const depNodes = workflow.nodes.filter((n) => node.dependsOn.includes(n.nodeId))

  const canValidate = node.statut === 'en_cours' || node.statut === 'a_faire' || node.statut === 'a_revoir'
  const canReopen = node.statut === 'valide'
  const canRequestRevision = node.statut === 'en_cours' || node.statut === 'valide'

  const STATUT_LABEL: Record<WorkflowNode['statut'], string> = {
    a_faire: 'À faire',
    en_cours: 'En cours',
    a_revoir: 'À revoir',
    valide: 'Validé',
    bloque: 'Bloqué',
  }

  const STATUT_CLASS: Record<WorkflowNode['statut'], string> = {
    a_faire: 'node-a-faire',
    en_cours: 'node-en-cours',
    a_revoir: 'node-a-revoir',
    valide: 'node-valide',
    bloque: 'node-bloque',
  }

  return (
    <>
      {/* Click-away backdrop */}
      <div
        className="drawer-backdrop"
        aria-hidden="true"
        onClick={onClose}
      />
      <aside
        ref={drawerRef}
        className="node-drawer"
        tabIndex={-1}
        role="complementary"
        aria-label={node.section}
      >
        <div className="drawer-header">
          <div style={{ flex: 1, minWidth: 0 }}>
            <span className={`wf-pill ${STATUT_CLASS[node.statut]}`}>
              {STATUT_LABEL[node.statut]}
            </span>
            {node.surCheminCritique && (
              <span className="wf-badge-critical" title={t('wf_critical_badge')}>
                {t('wf_critical_badge')}
              </span>
            )}
            {!node.enabled && (
              <span className="wf-badge-cond" title={t('wf_conditional_badge')}>
                {t('wf_conditional_badge')}
              </span>
            )}
          </div>
          <button className="drawer-close" aria-label={t('close')} onClick={onClose}>✕</button>
        </div>

        <h2 className="drawer-title">{node.section}</h2>

        {/* Owner */}
        {node.owner && (
          <p className="drawer-meta">
            <span className="drawer-meta-label">{t('wf_owner_label')}</span>
            {node.owner}
          </p>
        )}

        {/* Dependencies */}
        {depNodes.length > 0 && (
          <div className="drawer-meta">
            <span className="drawer-meta-label">{t('wf_deps_label')}</span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
              {depNodes.map((dep) => (
                <span
                  key={dep.nodeId}
                  className={`wf-dep-chip ${dep.statut === 'valide' ? 'dep-ok' : 'dep-wait'}`}
                >
                  {dep.statut === 'valide' ? '✓ ' : '○ '}
                  {dep.section}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Notes */}
        {node.notes && (
          <div className="drawer-meta">
            <span className="drawer-meta-label">{t('wf_notes_label')}</span>
            <p className="drawer-notes">{node.notes}</p>
          </div>
        )}

        {/* Artifact + Review room links */}
        <div className="drawer-rooms">
          <button
            className="btn"
            disabled={!node.artifactRoom}
            onClick={() => node.artifactRoom && onOpenRoom(node.artifactRoom)}
          >
            📄 {t('wf_open_artifact')}
          </button>
          <button
            className="btn"
            disabled={!node.reviewRoom}
            onClick={() => node.reviewRoom && onOpenRoom(node.reviewRoom)}
          >
            🔍 {t('wf_open_review')}
          </button>
        </div>

        {/* Actions */}
        <div className="drawer-actions">
          {canValidate && (
            <button
              className="btn primary"
              onClick={() => void patch({ statut: 'valide' })}
            >
              ✓ {t('wf_validate_btn')}
            </button>
          )}
          {canRequestRevision && (
            <button
              className="btn"
              onClick={() => void patch({ statut: 'a_revoir' })}
            >
              ↺ {t('wf_set_review_btn')}
            </button>
          )}
          {canReopen && (
            <button
              className="btn"
              onClick={() => void patch({ statut: 'en_cours' })}
            >
              {t('wf_reopen_btn')}
            </button>
          )}
          {node.skill && (
            <button
              className={`btn${generating ? ' disabled' : ''}`}
              disabled={generating || !node.enabled}
              onClick={() => void generate()}
              title={generating ? 'Génération en cours…' : `Générer via ${node.skill}`}
            >
              {generating ? '◐ Génération…' : '✦ Générer'}
            </button>
          )}
          {genError && (
            <span className="gen-error" role="alert" title={genError}>
              ⚠ {genError.slice(0, 60)}
            </span>
          )}
          <button
            className="btn"
            onClick={() => void patch({ enabled: !node.enabled })}
          >
            {node.enabled ? t('wf_disable_node') : t('wf_enable_node')}
          </button>
        </div>
      </aside>
    </>
  )
}
